<template>
  <div>
    <div
      class="tw-py-11 md:tw-py-11"
      :class="[
        containerClass,
        {
          'tw-container': !expand,
        },
      ]"
    >
      <!-- Section Title -->
      <div
        v-if="description || title"
        class="tw-flex tw-justify-center tw-mb-11"
      >
        <div class="tw-text-center tw-break-words">
          <!-- Title -->
          <component
            :is="titleTag"
            v-if="title"
            class="tw-text-mb-title-1 lg:tw-text-dt-xxl tw-mt-1"
          >
            {{ title }}
          </component>

          <!-- Description -->
          <p
            v-if="description"
            class="tw-text-mb-body-small lg:tw-text-dt-body tw-text-dark-gray"
          >
            {{ description }}
          </p>

          <!-- Title Underline -->
          <div
            v-if="titleUnderline"
            class="tw-flex tw-justify-center tw-items-center tw-mt-2"
          >
            <div class="tw-w-10 tw-h-1 tw-bg-secondary" />
          </div>
        </div>
      </div>
      <slot />
      <!-- See More Link — menuStore.list may be empty on SSR; wrap to suppress hydration mismatch -->
      <div data-allow-mismatch>
        <div
          v-if="seeMorePath && titleSeeMore"
          :class="`tw-text-center tw-pt-8 ${seeMoreClass}`"
        >
          <NuxtLink
            :to="seeMorePath"
            :title="$t('global.seeMore', { title })"
            class="tw-text-mb-button lg:tw-text-dt-button tw-text-primary see-more-link link-underline-hover hover:!tw-text-primary after:!tw-bg-primary"
          >
            <span
              @click="
                eventHomeSeeMore(
                  $t('global.seeMore', { title: titleSeeMore }),
                  eventName,
                )
              "
            >
              {{ $t("global.seeMore", { title: titleSeeMore }) }}
              <QIcon name="arrow_forward" />
            </span>
          </NuxtLink>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
const props = defineProps({
  titleTag: {
    type: String,
    default: 'h2',
  },
  seeMoreClass: {
    type: String,
    default: '',
  },
  description: {
    type: String,
    default: '',
  },
  title: {
    type: String,
    default: '',
  },
  seeMorePath: {
    type: String,
    default: '',
  },
  titleUnderline: {
    type: Boolean,
    default: false,
  },
  menuCode: {
    type: String,
    default: '',
  },
  eventName: {
    type: String,
    default: '',
  },
  expand: {
    type: Boolean,
    default: false,
  },
  containerClass: {
    type: String,
    default: '',
  },
});

const menuStore = useMenuStore();
const titleSeeMore = computed(() => {
  if (props.seeMorePath && props.menuCode) {
    return menuStore.list.find((l) => l.code === props.menuCode)?.title;
  }
  return null;
});

const { analyticEvent, fbPixelEvent } = useAnalytic();
const eventHomeSeeMore = (seeMoretitle: string, eventName: string) => {
  analyticEvent({
    ga4ActionName: eventName,
    ga4Label: seeMoretitle,
  });
  fbPixelEvent({
    fbCategory: 'trackCustom',
    fbEvent: `${props.title} - Home`,
    fbValue: { label: seeMoretitle },
  });
};
</script>

<style scoped>
/* Permanent underline in secondary */
.see-more-link::before {
  content: "";
  position: absolute;
  bottom: -4px;
  left: 0;
  width: 100%;
  height: 1px;
  background: var(--theme-color-light-gray-2);
}

/* Override link-underline-hover ::after to use scaleX for reverse animation */
.see-more-link.link-underline-hover::after {
  bottom: -4px;
  left: 0;
  width: 100% !important;
  height: 1px;
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.2s ease-in-out !important;
}

.see-more-link.link-underline-hover:hover::after {
  transform: scaleX(1);
}
</style>
